import { cn } from "@/lib/utils";
import { CSSProperties, useEffect, useId, useMemo, useRef, useState } from "react";
import { CoachAccent, CoachStatusKey, ariaLabelFromStatus, resolveCoachEventAccent, resolveCoachStatusAccent } from "./live-coach-signal.utils";

export type LiveCoachSignalEvent = {
    type: string;
    id: string;
    timestamp?: number;
    payload?: unknown;
};

export type LiveCoachSignalProps = {
    status: CoachStatusKey;
    lastEvent?: LiveCoachSignalEvent;
    label?: string;
    className?: string;
    compact?: boolean;
};

/** Largeur fixe du viewBox SVG. Le `preserveAspectRatio="none"` étire à la largeur réelle du conteneur. */
const VIEWBOX_W = 400;
/** Nombre de segments de chaque courbe — assez pour rester smooth, peu coûteux à recalculer. */
const SAMPLE_COUNT = 64;

/**
 * Le boost est volontairement décomposé en deux courbes indépendantes :
 *
 * - `motion` (amplitude + vitesse) est court et "physique" : une impulsion
 *   parabolique qui monte puis redescend à zéro en ~800 ms. C'est l'équivalent
 *   visuel d'un coup de pied dans l'onde — surtout pas une accélération
 *   maintenue qui ferait vibrer les courbes.
 * - `color` (overlay accent + glow) suit un profil "ADSR sans Sustain" :
 *   `ATTACK` : montée douce de 0 à 1 (évite le flash brutal du glow) ;
 *   `HOLD`   : palier à 100% (le temps de "lire" l'événement) ;
 *   `DECAY`  : décroissance lente vers 0.
 */
const BOOST_MOTION_MS = 800;
const BOOST_COLOR_ATTACK_MS = 220;
const BOOST_COLOR_HOLD_MS = 650;
const BOOST_COLOR_DECAY_MS = 1500;

/** Multiplicateurs appliqués au pic du boost mouvement — volontairement modérés. */
const BOOST_AMP_GAIN = 0.4;
const BOOST_SPEED_GAIN = 0.15;

/** Clamp du `dt` pour éviter un saut de phase énorme après un tab inactif. */
const MAX_FRAME_DT_MS = 64;

type WaveProfile = {
    /** Trois fréquences spatiales (rad/px) sommées pour obtenir un mouvement organique non-périodique. */
    k1: number;
    k2: number;
    k3: number;
    /** Multiplicateur de vitesse temporelle propre à la couche (variation entre courbes). */
    speedMul: number;
    /** Décalage de phase initial. */
    phaseOffset: number;
    /** Multiplicateur d'amplitude propre à la couche. */
    ampFactor: number;
    /** Opacité de base de la ligne. */
    opacity: number;
    /** Épaisseur du trait en unités viewBox. */
    strokeWidth: number;
};

/**
 * 3 couches sinusoïdales superposées. La couche 0 fait office de "principale",
 * les deux autres ajoutent des harmoniques décalées pour évoquer une présence
 * plus riche qu'une simple onde.
 */
const PROFILES: WaveProfile[] = [
    { k1: 0.04, k2: 0.074, k3: 0.023, speedMul: 0.9, phaseOffset: 0.0, ampFactor: 1.0, opacity: 0.95, strokeWidth: 1.4 },
    { k1: 0.032, k2: 0.059, k3: 0.02, speedMul: 0.75, phaseOffset: 1.5, ampFactor: 0.78, opacity: 0.55, strokeWidth: 1.0 },
    { k1: 0.049, k2: 0.094, k3: 0.016, speedMul: 1.05, phaseOffset: 3.0, ampFactor: 0.55, opacity: 0.35, strokeWidth: 0.8 }
];

const STATUS_BASE: Record<string, { amp: number; speed: number }> = {
    // Mêmes paramètres "muets" pour `not_connected` et `connecting` : onde
    // basse et lente, palette grise — visuellement on signale "rien ne se
    // passe, l'utilisateur doit agir".
    not_connected: { amp: 4.5, speed: 0.5 },
    connecting: { amp: 4.5, speed: 0.5 },
    idle: { amp: 10.0, speed: 1 },
    on_call: { amp: 15.0, speed: 3 },
    /*
     * `completed` = recap post-appel. Onde plus calme que `on_call` mais
     * un peu plus vivante que `idle` pour signaler "il s'est passé qqc,
     * tu peux relire" sans imiter l'écoute live.
     */
    completed: { amp: 6.0, speed: 0.6 }
};
const DEFAULT_STATUS_BASE = STATUS_BASE.idle;

const STATUS_BADGE_LABELS: Record<string, string> = {
    not_connected: "App desktop non connectée",
    connecting: "Connexion en cours",
    idle: "Attente d'un appel",
    on_call: "Écoute en cours",
    completed: "Appel terminé — recap"
};

function easeOutQuart(p: number): number {
    return 1 - Math.pow(1 - p, 4);
}

function easeOutCubic(p: number): number {
    return 1 - Math.pow(1 - p, 3);
}

/**
 * Enveloppe parabolique 4·p·(1-p) — pic à 1.0 au milieu, départ et arrivée à 0.
 * Donne une impulsion "physique" sans démarrage brutal ni coupure abrupte.
 */
function pulseEnvelope(p: number): number {
    if (p <= 0 || p >= 1) return 0;
    return 4 * p * (1 - p);
}

/** Intensité de l'overlay couleur : attack → hold → decay. Profil continu. */
function colorBoostStrength(elapsed: number): number {
    if (elapsed < 0) return 0;
    if (elapsed < BOOST_COLOR_ATTACK_MS) {
        return easeOutCubic(elapsed / BOOST_COLOR_ATTACK_MS);
    }
    const afterAttack = elapsed - BOOST_COLOR_ATTACK_MS;
    if (afterAttack < BOOST_COLOR_HOLD_MS) return 1;
    if (afterAttack < BOOST_COLOR_HOLD_MS + BOOST_COLOR_DECAY_MS) {
        return 1 - easeOutQuart((afterAttack - BOOST_COLOR_HOLD_MS) / BOOST_COLOR_DECAY_MS);
    }
    return 0;
}

/** Intensité de l'impulsion mouvement (amplitude + vitesse). Courte et symétrique. */
function motionBoostStrength(elapsed: number): number {
    if (elapsed < 0 || elapsed >= BOOST_MOTION_MS) return 0;
    return pulseEnvelope(elapsed / BOOST_MOTION_MS);
}

/**
 * Construit la chaîne `d` d'un `<path>` à partir d'un profil et d'un instant `timeMs`.
 * Une enveloppe sinus (sin(πx)) atténue les bords pour éviter les coupures abruptes.
 */
function buildWavePath(profile: WaveProfile, timeMs: number, baseAmp: number, midY: number, width: number): string {
    const amp = baseAmp * profile.ampFactor;
    const tSec = timeMs * 0.001 * profile.speedMul;
    let d = "";
    for (let i = 0; i <= SAMPLE_COUNT; i++) {
        const ratio = i / SAMPLE_COUNT;
        const x = ratio * width;
        const env = Math.sin(ratio * Math.PI);
        const w =
            Math.sin(x * profile.k1 + tSec * 0.9 + profile.phaseOffset) * 0.55 +
            Math.sin(x * profile.k2 + tSec * 1.4 + profile.phaseOffset * 1.3) * 0.3 +
            Math.sin(x * profile.k3 - tSec * 0.55 + profile.phaseOffset * 0.7) * 0.35;
        const y = midY + w * amp * env;
        d += i === 0 ? `M${x.toFixed(2)} ${y.toFixed(2)}` : `L${x.toFixed(2)} ${y.toFixed(2)}`;
    }
    return d;
}

function useReducedMotion(): boolean {
    const [reduced, setReduced] = useState<boolean>(() => {
        if (typeof window === "undefined" || !window.matchMedia) return false;
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    });
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const handler = (event: MediaQueryListEvent) => setReduced(event.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);
    return reduced;
}

function statusBadgeLabel(status: string): string {
    return STATUS_BADGE_LABELS[status] ?? status.replace(/_/g, " ");
}

export function LiveCoachSignal({ status, lastEvent, label = "Live Coach", className, compact = false }: LiveCoachSignalProps) {
    const reducedMotion = useReducedMotion();
    const uid = useId().replace(/:/g, "_");
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const pathRefs = useRef<Array<SVGPathElement | null>>([]);
    /** Timestamp `performance.now()` du dernier événement reçu, ou `-Infinity` si jamais. */
    const boostStartedAtRef = useRef<number>(-Infinity);
    /** Lecture vivante du status courant côté loop sans recréer le rAF à chaque changement. */
    const statusRef = useRef<string>(status);
    /**
     * Accumulateur de phase, en "ms-équivalent". On l'incrémente à chaque frame
     * avec `dt * speedFactor`. Cette intégration garantit une dérivée de phase
     * continue, même quand `speedFactor` varie pendant le boost — sans elle, on
     * obtient un effet de FM qui fait visiblement vibrer les courbes.
     */
    const phaseAccumRef = useRef<number>(0);
    const lastTickAtRef = useRef<number>(-1);

    const viewBoxH = compact ? 48 : 64;
    const midY = viewBoxH / 2;

    const statusAccent = useMemo<CoachAccent>(() => resolveCoachStatusAccent(status), [status]);
    const eventAccent = useMemo<CoachAccent | null>(() => (lastEvent?.type ? resolveCoachEventAccent(lastEvent.type) : null), [lastEvent?.type]);
    const overlayAccent = eventAccent ?? statusAccent;

    useEffect(() => {
        statusRef.current = status;
    }, [status]);

    /**
     * Gestion du boost à chaque nouvel événement :
     * - en mode normal, le rAF lit `boostStartedAtRef.current` à chaque frame
     *   (donc on n'a qu'à le mettre à jour ici, pas d'empilement possible) ;
     * - en mode reduced-motion, on simule manuellement un fade in/out sur la
     *   variable CSS `--coach-boost-overlay`.
     */
    useEffect(() => {
        if (!lastEvent?.id) return;
        boostStartedAtRef.current = typeof performance !== "undefined" ? performance.now() : Date.now();

        if (!reducedMotion) return;
        if (!wrapperRef.current) return;
        wrapperRef.current.style.setProperty("--coach-boost-overlay", "1");
        const handle = window.setTimeout(() => {
            if (wrapperRef.current) {
                wrapperRef.current.style.setProperty("--coach-boost-overlay", "0");
            }
        }, BOOST_COLOR_ATTACK_MS + BOOST_COLOR_HOLD_MS);
        return () => window.clearTimeout(handle);
    }, [lastEvent?.id, reducedMotion]);

    /**
     * Boucle d'animation unique :
     * - calcule l'amplitude / la vitesse effectives en fonction du status courant
     *   et du boost résiduel ;
     * - écrit les nouveaux `d` sur les `<path>` partagés via `setAttribute`
     *   (jamais de re-render React) ;
     * - met à jour la variable CSS `--coach-boost-overlay` qui pilote l'opacité
     *   de la couche d'accent et le glow.
     */
    useEffect(() => {
        if (reducedMotion) {
            const base = STATUS_BASE[statusRef.current] ?? DEFAULT_STATUS_BASE;
            for (let i = 0; i < PROFILES.length; i++) {
                const path = pathRefs.current[i];
                if (!path) continue;
                path.setAttribute("d", buildWavePath(PROFILES[i], 0, base.amp, midY, VIEWBOX_W));
            }
            if (wrapperRef.current) {
                wrapperRef.current.style.setProperty("--coach-boost-overlay", "0");
            }
            return;
        }

        let alive = true;
        let raf = 0;
        lastTickAtRef.current = -1;

        const tick = (now: number) => {
            if (!alive) return;

            const last = lastTickAtRef.current;
            const dt = last < 0 ? 16 : Math.min(MAX_FRAME_DT_MS, now - last);
            lastTickAtRef.current = now;

            const elapsed = boostStartedAtRef.current === -Infinity ? -1 : now - boostStartedAtRef.current;
            const motion = motionBoostStrength(elapsed);
            const color = colorBoostStrength(elapsed);

            const base = STATUS_BASE[statusRef.current] ?? DEFAULT_STATUS_BASE;
            const amp = base.amp * (1 + BOOST_AMP_GAIN * motion);
            const speedFactor = base.speed * (1 + BOOST_SPEED_GAIN * motion);

            phaseAccumRef.current += dt * speedFactor;

            for (let i = 0; i < PROFILES.length; i++) {
                const path = pathRefs.current[i];
                if (!path) continue;
                path.setAttribute("d", buildWavePath(PROFILES[i], phaseAccumRef.current, amp, midY, VIEWBOX_W));
            }

            if (wrapperRef.current) {
                wrapperRef.current.style.setProperty("--coach-boost-overlay", color.toFixed(3));
            }

            raf = requestAnimationFrame(tick);
        };

        raf = requestAnimationFrame(tick);

        return () => {
            alive = false;
            cancelAnimationFrame(raf);
        };
    }, [midY, reducedMotion]);

    /**
     * `--coach-stroke-active` interpole en continu entre la couleur status et
     * la couleur event en fonction de `--coach-boost-overlay` (0..1) que le
     * rAF met à jour. Le navigateur recalcule color-mix à chaque changement
     * de var, ce qui donne un swap de couleur **réel** des courbes (pas un
     * voile additif), beaucoup plus net qu'un overlay en `mix-blend-mode`.
     */
    const wrapperStyle = {
        "--coach-status-stroke": statusAccent.stroke,
        "--coach-status-glow": statusAccent.glow,
        "--coach-status-ring": statusAccent.ring,
        "--coach-event-stroke": overlayAccent.stroke,
        "--coach-event-glow": overlayAccent.glow,
        "--coach-stroke-active": "color-mix(in oklab, var(--coach-status-stroke), var(--coach-event-stroke) calc(var(--coach-boost-overlay, 0) * 100%))"
    } as CSSProperties;

    return (
        <div
            ref={wrapperRef}
            data-coach-signal=""
            data-status={status}
            role="status"
            aria-live="polite"
            aria-label={ariaLabelFromStatus(status, label)}
            className={cn("relative isolate mt-4 overflow-hidden  bg-white/85 py-2 backdrop-blur-sm transition-colors duration-300", "dark:border-white/10 dark:bg-gray-950/60", className)}
            style={{
                ...wrapperStyle,
                borderColor: "var(--coach-status-ring)"
            }}
        >
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10"
                style={{
                    background: "radial-gradient(120% 80% at 50% 50%, var(--coach-status-glow), transparent 65%)",
                    opacity: 0.35
                }}
            />
            {/*
             * Halo de fond "event" : superposé au halo status, son opacité
             * monte avec le boost. Quand l'event arrive, c'est lui qui domine
             * visuellement → le fond change vraiment de teinte.
             */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10"
                style={{
                    background: "radial-gradient(110% 75% at 50% 50%, var(--coach-event-glow), transparent 60%)",
                    opacity: "calc(var(--coach-boost-overlay, 0) * 0.85)"
                }}
            />

            {!compact ? (
                <div className="relative z-10 mx-2 flex items-center justify-center gap-2">
                    <span
                        className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                        style={{
                            color: "var(--coach-stroke-active)",
                            backgroundColor: "var(--coach-status-glow)"
                        }}
                    >
                        <span
                            aria-hidden
                            className="block size-1.5 rounded-full"
                            style={{
                                backgroundColor: "var(--coach-stroke-active)",
                                boxShadow: status === "on_call" ? "0 0 6px var(--coach-stroke-active)" : "none"
                            }}
                        />
                        {statusBadgeLabel(status)}
                    </span>
                </div>
            ) : null}

            <div className={cn("relative w-full", compact ? "h-8" : "mt-1.5 h-10")}>
                <svg viewBox={`0 0 ${VIEWBOX_W} ${viewBoxH}`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden="true">
                    <defs>
                        {PROFILES.map((_, i) => (
                            <path
                                key={`def-${i}`}
                                id={`coach-path-${uid}-${i}`}
                                ref={el => {
                                    pathRefs.current[i] = el;
                                }}
                                fill="none"
                            />
                        ))}
                        <filter id={`coach-blur-${uid}`} x="-10%" y="-30%" width="120%" height="160%">
                            <feGaussianBlur stdDeviation="2.4" />
                        </filter>
                    </defs>

                    {/* Halo flou sous les courbes — couleur dynamique color-mix. */}
                    <g filter={`url(#coach-blur-${uid})`} style={{ opacity: 0.55 }}>
                        {PROFILES.map((p, i) => (
                            <use
                                key={`halo-${i}`}
                                href={`#coach-path-${uid}-${i}`}
                                fill="none"
                                stroke="var(--coach-stroke-active)"
                                strokeWidth={p.strokeWidth + 3}
                                strokeLinecap="round"
                                opacity={p.opacity * 0.45}
                            />
                        ))}
                    </g>

                    {/*
                     * Couches principales — la couleur est swappée en continu
                     * via `--coach-stroke-active`. Un drop-shadow piloté par
                     * le boost vient ajouter un glow vif autour des traits au
                     * moment de l'event (intensité et couleur dynamiques).
                     */}
                    <g
                        style={{
                            filter: "drop-shadow(0 0 calc(var(--coach-boost-overlay, 0) * 7px) var(--coach-stroke-active))"
                        }}
                    >
                        {PROFILES.map((p, i) => (
                            <use
                                key={`main-${i}`}
                                href={`#coach-path-${uid}-${i}`}
                                fill="none"
                                stroke="var(--coach-stroke-active)"
                                strokeWidth={p.strokeWidth}
                                strokeLinecap="round"
                                opacity={p.opacity}
                            />
                        ))}
                    </g>
                </svg>
            </div>
        </div>
    );
}
